import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
} from "@mui/material";
import { useSelector } from "react-redux";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import AutohideSnackbar from "../../components/snackbar/Snackbar";
import API from "../../api/api";
import CreateIcon from '@mui/icons-material/Create';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
const Settings = ({ setShowSideBar }) => {
  const auth = useSelector((state) => state.user);
  const authType = auth.type;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(null); // Tracks which field is being edited
  const [editedValues, setEditedValues] = useState({}); // Stores edited values
  
  // XDTC Toggle states
  const [xdtcEnabled, setXdtcEnabled] = useState(false);
  const [xdtcLoading, setXdtcLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const fetchURLS = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await API.get(`/${authType}/settings`);
      if (response.data.success) {
        setData(response.data.obj);
      } else {
        setError("Failed to fetch settings.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "An error occurred while fetching settings.");
    } finally {
      setLoading(false);
    }
  }, [authType]);

  const fetchXdtcStatus = useCallback(async () => {
    try {
      const response = await API.get(`/${authType}/xdtc-status`);
      if (response.data.success) {
        setXdtcEnabled(response.data.xdtcEnabled);
      }
    } catch (err) {
      console.error("Failed to fetch XDTC status:", err);
    }
  }, [authType]);

  const handleXdtcToggle = async (event) => {
    const enabled = event.target.checked;
    setXdtcLoading(true);
    
    try {
      const response = await API.put(`/${authType}/toggle-xdtc`, { enabled });
      
      if (response.data.success) {
        setXdtcEnabled(response.data.xdtcEnabled);
        setSnackbar({
          open: true,
          message: response.data.message,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to update XDTC status',
          severity: 'error'
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'An error occurred while updating XDTC status',
        severity: 'error'
      });
    } finally {
      setXdtcLoading(false);
    }
  };

  const handleEdit = (key) => {
    setIsEditing(key);
    setEditedValues((prev) => ({
      ...prev,
      [key]: data[key], // Set the initial value of the key being edited
    }));
  };

  const handleSave = async (key) => {
    try {
      setLoading(true);
      const updatedData = { ...data, [key]: editedValues[key] };
      const response = await API.put(`/${authType}/settings`, { [key]: editedValues[key] });

      if (response.data.success) {
        setData(updatedData);
        setIsEditing(null);
        setSnackbar({
          open: true,
          message: 'Setting updated successfully',
          severity: 'success'
        });
      } else {
        setError("Failed to save changes.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "An error occurred while saving changes.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(null);
    setEditedValues({});
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    fetchURLS();
    fetchXdtcStatus();
  }, [fetchURLS, fetchXdtcStatus]);

  console.log("Data:", data);

  return (
   
    <>
      <Sidebar />
          <Navbar setShowSideBar={setShowSideBar} />
          <Container maxWidth="100" className="contailer-fluid" >
          <Typography sx={{
            mb:"30px"
           }} variant="h4" gutterBottom>
             Settings
           </Typography>
               <Box sx={{
                backgroundColor: "white",
                borderRadius: "10px",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                minHeight:"800px"
               }} p={4}>
        

         {error ? (
            <Box
              display="flex"
              justifyContent="center"
              flexDirection="column"
              gap={4}
              alignItems="center"
              minHeight="600px"
            >
              <Typography color="error" variant="body1">
                {error}
              </Typography>
              <Button onClick={fetchURLS} variant="contained">
                Try Again
              </Button>
            </Box>
          ) : loading && data.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="600px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* XDTC Toggle Section */}
              <Card elevation={2} sx={{ borderRadius: "10px", mb: 3 }}>
                <CardContent>
                  <ToggleSwitch
                    checked={xdtcEnabled}
                    onChange={handleXdtcToggle}
                    disabled={xdtcLoading}
                    loading={xdtcLoading}
                    label="XDTC System"
                    description="Turning this switch on will stop all orders from going to FTY if any if the orders in a PO is rejected/on-hold"
                  />
                </CardContent>
              </Card>

              <Divider sx={{ my: 3 }} />

              {/* Existing Settings */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                System Settings
              </Typography>
              
              <Grid container spacing={3}>
                {Object.entries(data)
                .filter(([key]) => key !== 'XDTC_BLOCK_MIXED_PO') 
                .map(([key, value]) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <Card elevation={2} sx={{ borderRadius: "10px" }}>
                      <CardContent>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, textTransform: "capitalize", marginBottom: 1 }}
                        >
                          {key.split("_").join(" ")}
                        </Typography>
                        {isEditing === key ? (
                          <TextField
                            fullWidth
                            size="small"
                            value={editedValues[key] || ""}
                            onChange={(e) =>
                              setEditedValues((prev) => ({ ...prev, [key]: e.target.value }))
                            }
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {value}
                          </Typography>
                        )}
                      </CardContent>
                      <CardActions>
                        {isEditing === key ? (
                          <>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSave(key)}
                              disabled={loading}
                              color="success"
                              sx={{ borderRadius: "50px" }}
                            >
                              <CheckCircleOutlineIcon />
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={handleCancel}
                              sx={{ borderRadius: "50px" }}
                            >
                              <HighlightOffIcon />
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleEdit(key)}
                            sx={{ borderRadius: "50px" }}
                          >
                            <CreateIcon />
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        
          
        </Box>
            
          </Container>

      {/* Snackbar for notifications */}
      <AutohideSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default Settings;
